/* =========================
   Numbers
   ========================= */

export function percent0dp(value) {
  return `${Number(value || 0).toFixed(0)}%`;
}

export function moneyGBP(value, dp = 0) {
  return Number(value || 0).toLocaleString("en-GB", {
    style: "currency",
    currency: "GBP",
    minimumFractionDigits: dp,
    maximumFractionDigits: dp,
  });
}

export function signedMoneyGBP(value, dp = 0) {
  const n = Number(value || 0);
  const sign = n >= 0 ? "+" : "-";
  return `${sign}${moneyGBP(Math.abs(n), dp)}`;
}

/* =========================
   Dates (safe, TZ-stable)
   ========================= */

function pad(n) {
  return String(n).padStart(2, "0");
}

/**
 * Parse yyyy-mm-dd as local noon to avoid TZ drift
 */
export function parseISODate(iso) {
  if (!iso) return null;
  const [y, m, d] = String(iso).split("-").map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d, 12, 0, 0, 0);
}

export function toISODate(input) {
  const d = input instanceof Date ? input : new Date(input);
  if (isNaN(d)) return "";
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export function addDaysISO(iso, n) {
  const d = parseISODate(iso);
  if (!d) return "";
  d.setDate(d.getDate() + n);
  return toISODate(d);
}

export function diffWeeksISO(isoA, isoB) {
  const a = parseISODate(isoA);
  const b = parseISODate(isoB);
  if (!a || !b) return 0;
  return Math.floor((b - a) / (7 * 24 * 60 * 60 * 1000));
}

export function formatDateDMY(input) {
  if (!input) return "";
  const d = input instanceof Date ? input : new Date(input);
  if (isNaN(d)) return "";
  return `${pad(d.getDate())}-${pad(d.getMonth() + 1)}-${d.getFullYear()}`;
}

export function formatWeekRange(startDate, endDate) {
  if (!startDate || !endDate) return "";
  return `${formatDateDMY(startDate)} → ${formatDateDMY(endDate)}`;
}

/**
 * Weekday key from ISO date (YYYY-MM-DD), TZ-stable (uses parseISODate)
 * Returns: "mon" | "tue" | "wed" | "thu" | "fri" | "sat" | "sun" | null
 */
export function weekdayKeyFromISO(iso) {
  const d = parseISODate(iso);
  if (!d) return null;

  // JS: 0 Sun .. 6 Sat
  const map = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];
  return map[d.getDay()] || null;
}

/* =========================
   Fiscal calendar (UK Apr FY, 4/4/5)
   ========================= */

const PERIOD_WEEKS = [4, 4, 5, 4, 4, 5, 4, 4, 5, 4, 4, 5];
const CUM_WEEKS = PERIOD_WEEKS.reduce((a, w) => {
  a.push((a.at(-1) || 0) + w);
  return a;
}, []);

export function firstSundayOnOrAfterApril1(year) {
  const d = new Date(year, 3, 1, 12, 0, 0, 0);
  const add = (7 - d.getDay()) % 7;
  d.setDate(d.getDate() + add);
  return toISODate(d);
}

export function fiscalYearStartForWeekEnding(weekEndingISO) {
  const d = parseISODate(weekEndingISO);
  if (!d) return "";
  const fyYear = d.getMonth() + 1 < 4 ? d.getFullYear() - 1 : d.getFullYear();
  return firstSundayOnOrAfterApril1(fyYear);
}

export function weekIndexInFY(weekEndingISO) {
  const fyStart = fiscalYearStartForWeekEnding(weekEndingISO);
  return diffWeeksISO(fyStart, weekEndingISO) + 1;
}

export function periodFromWeekIndex(weekIdx) {
  const w = Math.max(1, Math.min(52, Number(weekIdx || 1)));
  const periodNo = CUM_WEEKS.findIndex((c) => w <= c) + 1;
  const periodStartWeek = periodNo === 1 ? 1 : CUM_WEEKS[periodNo - 2] + 1;
  const periodEndWeek = CUM_WEEKS[periodNo - 1];
  const quarterNo = Math.ceil(periodNo / 3);
  const quarterEndWeek = CUM_WEEKS[quarterNo * 3 - 1];
  return { periodNo, periodStartWeek, periodEndWeek, quarterNo, quarterEndWeek };
}

export function fiscalContextForWeekEnding(weekEndingISO) {
  const fyStart = fiscalYearStartForWeekEnding(weekEndingISO);
  const weekIdx = weekIndexInFY(weekEndingISO);
  const p = periodFromWeekIndex(weekIdx);

  return {
    fyStart,
    fyEndDate: addDaysISO(fyStart, 51 * 7),
    weekIdx,
    ...p,
    periodStartDate: addDaysISO(fyStart, (p.periodStartWeek - 1) * 7),
    periodEndDate: addDaysISO(fyStart, (p.periodEndWeek - 1) * 7),
    quarterEndDate: addDaysISO(fyStart, (p.quarterEndWeek - 1) * 7),
  };
}

export function currentWeekEndingISO() {
  const d = new Date();
  d.setHours(12, 0, 0, 0);
  d.setDate(d.getDate() + ((7 - d.getDay()) % 7));
  return toISODate(d);
}

/* =========================
   Compatibility exports
   =========================
   Older components probe several historic helper names. Keeping these aliases
   here gives them one canonical implementation and avoids webpack missing-export
   warnings.
*/

export function formatCurrency(value, dp = 2) {
  return moneyGBP(value, dp);
}

export const formatMoney = formatCurrency;
export const money = formatCurrency;
export const currency = formatCurrency;
export const toCurrency = formatCurrency;

function asLocalDate(input) {
  if (input instanceof Date) return input;

  const value = String(input || "").trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return parseISODate(value);
  }

  const d = new Date(input);
  return isNaN(d) ? null : d;
}

export function formatDateShort(input) {
  const d = asLocalDate(input);
  if (!d) return "";
  return `${pad(d.getDate())} ${d.toLocaleString("en-GB", { month: "short" })}`;
}

export const formatShortDate = formatDateShort;
export const shortDate = formatDateShort;
export const displayDateShort = formatDateShort;

export const toISO = toISODate;
export const formatISODate = toISODate;
export const isoDate = toISODate;

export function formatUK(input) {
  const d = asLocalDate(input);
  if (!d) return "";
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
}

export const formatUKDate = formatUK;
export const ukDate = formatUK;
export const formatDateUK = formatUK;
